import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, ShieldCheck, Zap, Users, CalendarCheck } from 'lucide-react';

const RevenueTestimonialSection = () => {
  const features = [
    { icon: <Users className="w-5 h-5 text-blue-400" />, title: "Increase Production", description: "Capture new patients 24/7 with AI Receptionist, reactivate patients via Hygiene Recall, and boost Treatment Acceptance." },
    { icon: <CalendarCheck className="w-5 h-5 text-indigo-400" />, title: "Differentiate Revenue", description: "Schedule Optimizer catches significantly more no-shows and fills cancellations upfront relative to basic text reminders." },
    { icon: <ShieldCheck className="w-5 h-5 text-emerald-400" />, title: "Defend Revenue", description: "Intelligent Insurance Verification prevents write-offs before they happen, ensuring you get paid for every procedure." },
    { icon: <Zap className="w-5 h-5 text-amber-400" />, title: "Accelerate Revenue", description: "AI-enabled EOB posting and automated claims processing reduce days in AR significantly and streamline cash flow." },
  ];

  return (
    <section className="py-24 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="relative rounded-3xl overflow-hidden bg-[#0A0A12] text-white p-10 md:p-16 flex flex-col items-center text-center border border-white/[0.06]">
          {/* Background Gradients */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-[-30%] right-[-15%] w-[50%] h-[60%] bg-blue-600/10 rounded-full blur-[100px]" />
            <div className="absolute bottom-[-30%] left-[-15%] w-[50%] h-[60%] bg-indigo-600/10 rounded-full blur-[100px]" />
          </div>

          <div className="relative z-10 max-w-5xl mx-auto flex flex-col items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.05] border border-white/[0.08] backdrop-blur-sm mb-8"
            >
              <TrendingUp className="w-4 h-4 text-green-400" />
              <span className="text-sm font-medium text-green-300/80">Proven ROI</span>
            </motion.div>

            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
              className="text-3xl md:text-5xl font-bold mb-6 leading-tight tracking-tight"
            >
              The average practice increases revenue by<br />
              <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">$7,700+ in the first month</span>
            </motion.h2>

            <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
              className="text-base md:text-lg text-white/40 mb-14 max-w-3xl leading-relaxed"
            >
              We're the only company in the world with <span className="text-white font-medium">14+ AI agents</span> to transform every part of your dental practice. 
              Our collaborative digital workforce drives production and prevents revenue loss across your entire office.
            </motion.p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 w-full text-left">
              {features.map((feature, index) => (
                <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.3 + (index * 0.08) }}
                  className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-6 hover:bg-white/[0.06] hover:border-white/[0.1] transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06] shrink-0">{feature.icon}</div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1.5">{feature.title}</h3>
                      <p className="text-white/35 leading-relaxed text-sm">{feature.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RevenueTestimonialSection;
